import type { MeasurementStrategyContext, PdpMeasurementStrategy } from './PdpMeasurementStrategy';

/** Shopware's zoom modal (`.js-zoom-modal`) is a pre-rendered, hidden Bootstrap modal; only `shown.bs.modal` means a real open. */
const ZOOM_MODAL_SELECTOR = '.js-zoom-modal';
/** Bootstrap 5 dispatches modal lifecycle events as bubbling events, so one delegated listener on `document` catches them. */
const MODAL_SHOWN_EVENT = 'shown.bs.modal';
/** The magnifier *container* exists at load; the zoom-image node itself is created only while the user actively hovers. */
const MAGNIFIER_ZOOM_IMAGE_SELECTOR = '.js-magnifier-zoom-image';

/**
 * Counts `image.zoomed` only on a genuine user zoom, never on load-time DOM state. Two Shopware 6.6
 * signals feed it: (1) the zoom modal opening — a bubbling `shown.bs.modal` whose target is the
 * `.js-zoom-modal` element (click/tap → fullscreen); (2) the magnifier lens engaging — the
 * `.js-magnifier-zoom-image` node, which `MagnifierPlugin` creates on real hover and removes on
 * mouseout. We deliberately do NOT scan for container presence at attach (the previous behaviour),
 * because those containers are pre-rendered hidden and produced a false `zoomed=1` on the first beat.
 *
 * Plugin coverage: core gallery (magnifier + zoom modal). Themes/extensions that replace these with
 * different open events or node names need an added signal here rather than a presence selector.
 */
export class GalleryZoomStrategy implements PdpMeasurementStrategy {
  private ctx: MeasurementStrategyContext | null = null;

  private mutationObserver: MutationObserver | null = null;

  private onModalShown: ((event: Event) => void) | null = null;

  /** Rising-edge guard so a magnifier session that spans several DOM mutations counts once. */
  private magnifierActive = false;

  attach(ctx: MeasurementStrategyContext): void {
    this.ctx = ctx;
    const { events, store, document: doc } = ctx;

    this.onModalShown = (event: Event) => {
      const target = event.target as Element | null;
      if (target?.matches?.(ZOOM_MODAL_SELECTOR)) {
        store.incrementImageZoomed();
      }
    };
    events.on(doc, MODAL_SHOWN_EVENT, this.onModalShown);

    const body = doc.body;
    if (!body) {
      return;
    }

    this.mutationObserver = new MutationObserver(() => {
      this.recomputeMagnifier();
    });
    this.mutationObserver.observe(body, { childList: true, subtree: true });
  }

  private recomputeMagnifier(): void {
    if (!this.ctx) {
      return;
    }
    const present = this.ctx.document.querySelector(MAGNIFIER_ZOOM_IMAGE_SELECTOR) != null;
    if (present && !this.magnifierActive) {
      this.ctx.store.incrementImageZoomed();
    }
    this.magnifierActive = present;
  }

  dispose(): void {
    if (this.ctx != null && this.onModalShown != null) {
      this.ctx.events.off(this.ctx.document, MODAL_SHOWN_EVENT, this.onModalShown);
    }
    this.onModalShown = null;
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.magnifierActive = false;
    this.ctx = null;
  }
}
